import './WorkbenchPages.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import http from '../../../api/http';
import PageHero from '../../../components/page/PageHero';
import StatusBadge from '../../../components/ui/StatusBadge';

const STATUSES = ['draft', 'merging', 'merged', 'discarded', 'failed'];
const NUMERIC_TYPES = ['number', 'integer', 'decimal', 'float', 'currency', 'percent'];

const messageOf = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
const when = (value) => (value ? new Date(value).toLocaleString() : '—');
const keyOf = (section, id) => `${section}:${id}`;

const coerce = (raw, type) => {
  if (raw === '') return null;
  if (NUMERIC_TYPES.includes(String(type || '').toLowerCase())) {
    const number = Number(raw);
    return Number.isFinite(number) ? number : raw;
  }
  if (String(type || '').toLowerCase() === 'boolean') return raw === 'true';
  return raw;
};

const flattenTree = (node, out = []) => {
  if (!node) return out;
  out.push({ section: node.section, id: String(node.id), label: node.label ?? node.id, status: node.status });
  Object.values(node.children || {}).forEach((group) => group.forEach((child) => flattenTree(child, out)));
  return out;
};

const fieldsFor = (node) => {
  if (!node) return [];
  const fields = node.fields.filter((field) => field.type !== 'relation');
  return node.statusOptions?.length && !fields.some((field) => field.handle === 'status')
    ? [...fields, { handle: 'status', label: 'Status', type: 'status' }]
    : fields;
};

const ValueInput = ({ field, value, onChange }) => {
  if (field?.handle === 'status' && field.options?.length) {
    return (
      <select aria-label="Value" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Choose status…</option>
        {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    );
  }
  if (String(field?.type || '').toLowerCase() === 'boolean') {
    return (
      <select aria-label="Value" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  const type = NUMERIC_TYPES.includes(String(field?.type || '').toLowerCase())
    ? 'number'
    : String(field?.type || '').toLowerCase() === 'date' ? 'date' : 'text';
  return <input aria-label="Value" type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder="New value" />;
};

const NewScenarioForm = ({ nodes, onCreated }) => {
  const [title, setTitle] = useState('');
  const [section, setSection] = useState('');
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState('');
  const [error, setError] = useState('');
  const node = nodes.find((item) => item.section === section);

  useEffect(() => {
    setRecordId('');
    setRecords([]);
    if (!node?.slug) return undefined;
    let active = true;
    http.get(`/${node.slug}`, { params: { limit: 100, page: 1, sort: 'updatedAt', order: 'DESC' } })
      .then(({ data }) => { if (active) setRecords(Array.isArray(data?.data) ? data.data : []); })
      .catch(() => { if (active) setRecords([]); });
    return () => { active = false; };
  }, [node?.slug]);

  async function submit(event) {
    event.preventDefault();
    setError('');
    try {
      const { data } = await http.post('/scenarios', { title, root: { model: section, id: recordId } });
      setTitle('');
      onCreated(data);
    } catch (requestError) {
      setError(messageOf(requestError, 'Unable to create scenario.'));
    }
  }

  return (
    <form className="workbench-form" onSubmit={submit}>
      <input aria-label="Scenario title" value={title} maxLength={160} onChange={(event) => setTitle(event.target.value)} placeholder="What if…" />
      <select aria-label="Root model" value={section} onChange={(event) => setSection(event.target.value)}>
        <option value="">Root model…</option>
        {nodes.map((item) => <option key={item.section} value={item.section}>{item.label}</option>)}
      </select>
      <select aria-label="Root record" value={recordId} onChange={(event) => setRecordId(event.target.value)} disabled={!section}>
        <option value="">Root record…</option>
        {records.map((record) => (
          <option key={record.id} value={record.id}>{(node?.handle && record[node.handle]) || record.id}</option>
        ))}
      </select>
      <button className="button-primary" type="submit" disabled={!title.trim() || !recordId}>Start scenario</button>
      {error ? <p className="error-text" role="alert">{error}</p> : null}
    </form>
  );
};

const AddChangeForm = ({ graph, scope, created, onAdd }) => {
  const [action, setAction] = useState('update');
  const [target, setTarget] = useState('');
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [childLink, setChildLink] = useState('');
  const [extraField, setExtraField] = useState('');
  const [extraValue, setExtraValue] = useState('');

  const nodeOf = (section) => graph.nodes.find((item) => item.section === section);
  const parents = [...scope, ...created];
  const targetRecord = (action === 'create' ? parents : scope).find((item) => keyOf(item.section, item.id) === target) || null;
  const targetNode = nodeOf(targetRecord?.section);
  const fieldOptions = fieldsFor(targetNode).map((item) => (item.handle === 'status' ? { ...item, options: targetNode.statusOptions } : item));
  const selectedField = fieldOptions.find((item) => item.handle === field);
  const childLinks = targetRecord ? graph.links.filter((link) => link.parent === targetRecord.section) : [];
  const link = childLinks.find((item) => item.key === childLink);
  const childNode = nodeOf(link?.child);
  const childExtraFields = fieldsFor(childNode)
    .filter((item) => item.handle !== childNode?.handle && item.handle !== link?.foreignKey)
    .map((item) => (item.handle === 'status' ? { ...item, options: childNode.statusOptions } : item));
  const selectedExtra = childExtraFields.find((item) => item.handle === extraField);

  const reset = () => {
    setField('');
    setValue('');
    setChildLink('');
    setExtraField('');
    setExtraValue('');
  };

  function submit(event) {
    event.preventDefault();
    if (!targetRecord) return;
    if (action === 'update') {
      onAdd({ section: targetRecord.section, action, recordId: targetRecord.id, patch: { [field]: coerce(value, selectedField?.type) } });
    } else if (action === 'delete') {
      onAdd({ section: targetRecord.section, action, recordId: targetRecord.id });
    } else {
      const patch = { [link.foreignKey]: targetRecord.id };
      if (childNode?.handle) patch[childNode.handle] = value;
      if (extraField) patch[extraField] = coerce(extraValue, selectedExtra?.type);
      onAdd({ section: link.child, action, patch });
    }
    reset();
  }

  const ready = targetRecord && (
    action === 'delete'
    || (action === 'update' && field)
    || (action === 'create' && link && (!childNode?.handle || value.trim()))
  );

  return (
    <form className="workbench-form" onSubmit={submit}>
      <select aria-label="Change type" value={action} onChange={(event) => { setAction(event.target.value); setTarget(''); reset(); }}>
        <option value="update">Change a field</option>
        <option value="create">Add a child record</option>
        <option value="delete">Remove a record</option>
      </select>
      <select aria-label={action === 'create' ? 'Parent record' : 'Record'} value={target} onChange={(event) => { setTarget(event.target.value); reset(); }}>
        <option value="">{action === 'create' ? 'Under parent…' : 'Record…'}</option>
        {(action === 'create' ? parents : scope).map((item) => (
          <option key={keyOf(item.section, item.id)} value={keyOf(item.section, item.id)}>
            {item.section}: {item.label}{item.pending ? ' (scenario)' : ''}
          </option>
        ))}
      </select>
      {action === 'update' && targetRecord ? (
        <>
          <select aria-label="Field" value={field} onChange={(event) => { setField(event.target.value); setValue(''); }}>
            <option value="">Field…</option>
            {fieldOptions.map((item) => <option key={item.handle} value={item.handle}>{item.label}</option>)}
          </select>
          {field ? <ValueInput field={selectedField} value={value} onChange={setValue} /> : null}
        </>
      ) : null}
      {action === 'create' && targetRecord ? (
        <>
          <select aria-label="Child model" value={childLink} onChange={(event) => { setChildLink(event.target.value); setValue(''); setExtraField(''); }}>
            <option value="">Child type…</option>
            {childLinks.map((item) => (
              <option key={item.key} value={item.key}>{nodeOf(item.child)?.label || item.child} (via {item.foreignKey})</option>
            ))}
          </select>
          {link && childNode?.handle ? (
            <input aria-label="Name" value={value} onChange={(event) => setValue(event.target.value)} placeholder={childNode.fields.find((item) => item.handle === childNode.handle)?.label || 'Name'} />
          ) : null}
          {link ? (
            <select aria-label="Extra field" value={extraField} onChange={(event) => { setExtraField(event.target.value); setExtraValue(''); }}>
              <option value="">Also set…</option>
              {childExtraFields.map((item) => <option key={item.handle} value={item.handle}>{item.label}</option>)}
            </select>
          ) : null}
          {extraField ? <ValueInput field={selectedExtra} value={extraValue} onChange={setExtraValue} /> : null}
        </>
      ) : null}
      <button className="button-primary" type="submit" disabled={!ready}>Add change</button>
    </form>
  );
};

const HealthCell = ({ health }) => (health
  ? <span><StatusBadge value={health.status} /> {health.score ?? '—'}</span>
  : <span className="small-muted">—</span>);

const ScenarioDetail = ({ scenarioId, graph, onChanged }) => {
  const [scenario, setScenario] = useState(null);
  const [scope, setScope] = useState([]);
  const [impact, setImpact] = useState(null);
  const [override, setOverride] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const refreshImpact = useCallback(() => http.get(`/scenarios/${scenarioId}/impact`)
    .then(({ data }) => setImpact(data))
    .catch((requestError) => {
      setImpact(null);
      setError(messageOf(requestError, 'Unable to compute impact.'));
    }), [scenarioId]);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data } = await http.get(`/scenarios/${scenarioId}`);
      setScenario(data);
      const tree = await http.get(`/foundations/records/${encodeURIComponent(data.rootSection)}/${encodeURIComponent(data.rootId)}/descendants`, { params: { depth: 6 } })
        .then((response) => response.data)
        .catch(() => null);
      setScope(flattenTree(tree));
      await refreshImpact();
    } catch (requestError) {
      setError(messageOf(requestError, 'Unable to load scenario.'));
    }
  }, [scenarioId, refreshImpact]);

  useEffect(() => { load(); }, [load]);

  const labels = useMemo(() => new Map(scope.map((item) => [keyOf(item.section, item.id), item.label])), [scope]);
  const created = useMemo(() => (scenario?.changes || [])
    .filter((change) => change.action === 'create')
    .map((change) => {
      const handle = graph.nodes.find((item) => item.section === change.section)?.handle;
      return { section: change.section, id: change.recordId, label: (handle && change.patch?.[handle]) || change.recordId, pending: true };
    }), [scenario, graph]);
  const createdLabels = useMemo(() => new Map(created.map((item) => [keyOf(item.section, item.id), item.label])), [created]);
  const labelOf = (section, id) => labels.get(keyOf(section, id)) || createdLabels.get(keyOf(section, id)) || id;

  async function run(task, success) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await task();
      if (success) setNotice(typeof success === 'function' ? success(result) : success);
      return result;
    } catch (requestError) {
      setError(messageOf(requestError, 'Request failed.'));
      if (requestError?.response?.status === 409) await refreshImpact();
      return null;
    } finally {
      setBusy(false);
    }
  }

  const addChange = (change) => run(async () => {
    const { data } = await http.post(`/scenarios/${scenarioId}/changes`, { changes: [change] });
    setScenario(data);
    await refreshImpact();
    onChanged();
  });

  const removeChange = (changeId) => run(async () => {
    const { data } = await http.delete(`/scenarios/${scenarioId}/changes/${changeId}`);
    setScenario(data);
    await refreshImpact();
    onChanged();
  });

  const merge = () => run(async () => {
    const { data } = await http.post(`/scenarios/${scenarioId}/merge`, { override });
    await load();
    onChanged();
    return data;
  }, (data) => `Merged ${data?.mergeResult?.applied?.length ?? data?.changes?.length ?? 0} change(s) into live data.`);

  const discard = () => run(async () => {
    await http.post(`/scenarios/${scenarioId}/discard`);
    await load();
    onChanged();
  }, 'Scenario discarded.');

  if (!scenario) return <section className="card">{error ? <p className="error-text" role="alert">{error}</p> : <p>Loading scenario…</p>}</section>;

  const draft = scenario.status === 'draft';
  const conflicts = impact?.conflicts || [];
  const health = impact?.preview?.health;
  const forecasts = impact?.preview?.forecasts || [];

  return (
    <section className="card stack">
      <div className="row-between">
        <div>
          <h2 className="section-title">{scenario.title}</h2>
          <p className="small-muted">
            Root {scenario.rootSection}: {labelOf(scenario.rootSection, scenario.rootId)} · based on live data at {when(scenario.baseAt)}
          </p>
        </div>
        <StatusBadge value={scenario.status} />
      </div>
      {error ? <p className="error-text" role="alert">{error}</p> : null}
      {notice ? <p className="workbench-ok">{notice}</p> : null}
      {scenario.mergeResult ? <pre className="workbench-pre">{JSON.stringify(scenario.mergeResult, null, 2)}</pre> : null}

      <h3 className="section-title">Changes</h3>
      {scenario.changes.length ? (
        <ul className="workbench-list">
          {scenario.changes.map((change) => (
            <li key={change.id} className="row-between">
              <span>
                <strong>{change.action}</strong> {change.section} {change.action === 'create' ? `(${change.recordId})` : labelOf(change.section, change.recordId)}
                {change.patch ? <span className="small-muted"> {JSON.stringify(change.patch)}</span> : null}
              </span>
              {draft ? <button className="button-secondary" type="button" disabled={busy} onClick={() => removeChange(change.id)}>Remove</button> : null}
            </li>
          ))}
        </ul>
      ) : <p className="small-muted">No changes yet. Nothing here touches live data until you merge.</p>}
      {draft ? <AddChangeForm graph={graph} scope={scope} created={created} onAdd={addChange} /> : null}

      <h3 className="section-title">Impact</h3>
      {health ? (
        <div className="card-grid metrics-grid">
          <article className="card metric-card">
            <p className="hero-stat-label">Health now</p>
            <HealthCell health={health.before} />
          </article>
          <article className="card metric-card">
            <p className="hero-stat-label">Health with scenario</p>
            <HealthCell health={health.after} />
          </article>
          <article className="card metric-card">
            <p className="hero-stat-label">Score change</p>
            <strong>{health.scoreDelta === null ? '—' : `${health.scoreDelta > 0 ? '+' : ''}${health.scoreDelta}`}</strong>
            {health.statusChanged ? <p className="workbench-warning">Status changes</p> : null}
          </article>
        </div>
      ) : <p className="small-muted">Impact unavailable.</p>}
      {forecasts.length ? (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sprint pack</th>
                <th>Forecast now</th>
                <th>Forecast with scenario</th>
                <th>Shift (days)</th>
                <th>Remaining tasks Δ</th>
              </tr>
            </thead>
            <tbody>
              {forecasts.map((item) => (
                <tr key={item.sprintPackId}>
                  <td>{labelOf('SprintPack', item.sprintPackId)}</td>
                  <td>{item.before?.forecastDate || item.before?.status || '—'}</td>
                  <td>{item.after?.forecastDate || item.after?.status || '—'}</td>
                  <td>{item.forecastDateDeltaDays ?? '—'}</td>
                  <td>{item.remainingTasksDelta ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <h3 className="section-title">Conflicts with live edits</h3>
      {conflicts.length ? (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Record</th>
                <th>Field</th>
                <th>Kind</th>
                <th>At scenario start</th>
                <th>Live now</th>
                <th>Scenario</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((item, index) => (
                <tr key={`${item.section}:${item.recordId}:${item.field}:${index}`}>
                  <td>{item.section}: {labelOf(item.section, item.recordId)}</td>
                  <td>{item.field || '—'}</td>
                  <td>{item.kind.replace(/_/g, ' ')}</td>
                  <td>{JSON.stringify(item.base ?? null)}</td>
                  <td>{JSON.stringify(item.live ?? null)}</td>
                  <td>{JSON.stringify(item.scenario ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="small-muted">No live edits collide with this scenario.</p>}

      {draft ? (
        <div className="workbench-actions">
          {conflicts.length ? (
            <label>
              <input type="checkbox" checked={override} onChange={(event) => setOverride(event.target.checked)} />
              {' '}Overwrite live edits with scenario values
            </label>
          ) : null}
          <button
            className="button-primary"
            type="button"
            disabled={busy || !scenario.changes.length || (conflicts.length > 0 && !override)}
            onClick={merge}
          >
            Merge into live data
          </button>
          <button className="button-secondary" type="button" disabled={busy} onClick={discard}>Discard</button>
        </div>
      ) : null}
    </section>
  );
};

const ScenariosPage = () => {
  const [graph, setGraph] = useState({ nodes: [], links: [] });
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('draft');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');

  const loadList = useCallback(() => http.get('/scenarios', { params: status ? { status } : {} })
    .then(({ data }) => setItems(Array.isArray(data?.items) ? data.items : []))
    .catch((requestError) => setError(messageOf(requestError, 'Unable to load scenarios.'))), [status]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    http.get('/foundations/graph')
      .then(({ data }) => setGraph({ nodes: data?.nodes || [], links: data?.links || [] }))
      .catch((requestError) => setError(messageOf(requestError, 'Unable to load the model graph.')));
  }, []);

  const rootNodes = useMemo(
    () => graph.nodes.filter((node) => graph.links.some((link) => link.parent === node.section)),
    [graph],
  );

  return (
    <div className="stack">
      <PageHero title="Scenarios" subtitle="Sketch changes to a plan, see their effect on health and forecasts, then merge or throw them away." />
      {error ? <p className="error-text" role="alert">{error}</p> : null}

      <section className="card stack">
        <h2 className="section-title">New scenario</h2>
        <p className="small-muted">Pick the record whose subtree you want to experiment with. Changes stay in the scenario until merged.</p>
        <NewScenarioForm nodes={rootNodes} onCreated={(scenario) => { setStatus('draft'); setSelectedId(scenario.id); loadList(); }} />
      </section>

      <div className="workbench-columns">
        <section className="card stack">
          <div className="row-between">
            <h2 className="section-title">Scenarios</h2>
            <select aria-label="Status filter" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All</option>
              {STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          {items.length ? (
            <ul className="workbench-list">
              {items.map((item) => (
                <li key={item.id} className="row-between">
                  <button
                    className={`button-secondary${item.id === selectedId ? ' workbench-selected' : ''}`}
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                  >
                    {item.title}
                  </button>
                  <span className="small-muted">{item.rootSection} · {when(item.updatedAt)} <StatusBadge value={item.status} /></span>
                </li>
              ))}
            </ul>
          ) : <p className="small-muted">No {status || ''} scenarios.</p>}
        </section>
        {selectedId
          ? <ScenarioDetail key={selectedId} scenarioId={selectedId} graph={graph} onChanged={loadList} />
          : <section className="card"><p className="small-muted">Select or start a scenario.</p></section>}
      </div>
    </div>
  );
};

export default ScenariosPage;
